// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import { PropsWithChildren } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, Tooltip } from '@mui/material';

const CopyToClipboard = ({
  children,
  buttonClassName,
  tooltipText,
  value,
}: PropsWithChildren<{
  buttonClassName: string;
  tooltipText: string;
  value: string;
}>) => {
  const { enqueueSnackbar } = useSnackbar();

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(value)
      .then(() => enqueueSnackbar('Successfully copied to clipboard', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' }));
  };

  return (
    <>
      {/* Display the copy button in case the value is not empty */}
      {value && (
        <Tooltip title={tooltipText} arrow>
          <Button
            className={buttonClassName}
            color="inherit"
            size="small"
            variant="text"
            startIcon={<ContentCopyIcon />}
            onClick={copyToClipboard}
            sx={{ textTransform: 'none' }}
          >
            {children}
          </Button>
        </Tooltip>
      )}
      <div style={{ display: 'none' }} aria-hidden="true" aria-label="user-primary-party-id">
        {value}
      </div>
    </>
  );
};

export default CopyToClipboard;
